/**
 * SpineTagStrip — per-kind inline tag pills on a spine row.
 *
 * Spec 23 P4 · replaces SpineBadge (collapsed total pill) with the
 * per-kind vocabulary the design prototype specifies
 * (``variant-c-scenes.jsx`` scenes 42+). Each kind with a positive
 * counter renders its own tinted pill inline; kinds with zero
 * counters render nothing. The whole strip self-hides when every
 * counter is zero — absence is the signal.
 *
 * Rendered variants (order = visual priority, highest attention
 * first; colors come from tokens + two design-cyan / design-blue
 * entries inlined here per the scenes file):
 *
 *   regen × N      amber           "Regenerated N time(s)"
 *   🌐 × N         cyan    #06B6D4 "N external web search(es)"
 *   cite × N       brand primary   "N citation swap(s)"
 *   Q × N          brand mid       "Writer asked N question(s)"
 *   📎 Word × N    blue    #1565C0 "N writer edit(s) in Word"
 *   📚 × N         warn            "N library change(s)"
 *
 * Capped at MAX_VISIBLE pills visible; overflow collapses to a
 * "+K more" chip with aggregate title for screen-readers + hover.
 *
 * SRP: purely presentational. Consumes ``SpineCounters`` (from
 * ``eventLog.ts``); emits nothing. Color + glyph vocabulary is a
 * local constant table — OCP when a new EventKind joins the
 * counter set: add one row to ``TAG_SPECS`` + (if the new kind
 * needs its own counter) extend SpineCounters + updateCounters.
 *
 * # DECISION: hide the strip entirely when every counter is zero.
 * # WHY: 38-section CSR spines keep most rows quiet. Emitting a
 * #      muted "0 events" shell on every queued row was noise the
 * #      collapsed-pill version (SpineBadge) got right and the
 * #      design explicitly carries forward.
 * # DATE: 2026-04-22
 *
 * # DECISION: inline priority ordering (regen > web > cite > Q >
 * #   Word > library) rather than raw counter magnitude.
 * # WHY: attention-directing — regens signal the most engagement
 * #      with the row ("this section had multiple attempts"), web
 * #      signals external research, cite signals factual revision.
 * #      Magnitude-based ordering (say, 5 Word edits dwarfing 1
 * #      regen) would bury the higher-signal kind. Fixed order
 * #      matches the design prototype's treatment.
 * # DATE: 2026-04-22
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { describeTagCounts, selectVisibleTags, totalTagCount } from "../counterVocab";
import type { SpineCounters } from "../eventLog";
import { fonts, inkColors, surfaceColors } from "../tokens";

// Re-exports for callers that already imported via SpineTagStrip (kept
// so Spec 17 P3 tests + any external consumers keep working without
// churn). Canonical home is now ``counterVocab.ts``.
export { totalTagCount, describeTagCounts, selectVisibleTags };

/** Max pills visible inline on a spine row; overflow chip carries the rest. */
export const MAX_VISIBLE_TAGS = 3;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  strip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    padding: "2px 5px",
    borderRadius: "10px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
    cursor: "help",
  },
  overflow: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 5px",
    borderRadius: "10px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.lineStrong}`,
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: inkColors.ink4,
    lineHeight: 1,
    cursor: "help",
  },
  // Spec 23 P6 — auto-star marker when quality is high AND section
  // has at least one regen. Rendered as a trailing glyph on the
  // strip (no pill chrome) so it reads as a decorative accent.
  star: {
    fontSize: "11px",
    lineHeight: 1,
    cursor: "help",
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface SpineTagStripProps {
  counters: SpineCounters;
  /** Spec 23 P6 — when true, an ⭐ marker is appended to the strip.
   *  Computed by the host (LoomSpine) from section.quality +
   *  counters.regens; not derived here so the star condition stays
   *  in one place. */
  showStar?: boolean;
}

export const SpineTagStrip: React.FC<SpineTagStripProps> = ({ counters, showStar = false }) => {
  const styles = useStyles();
  // Hide the whole strip when there is nothing to summarise AND no
  // star to render. Preserves the "absence is the signal" contract
  // for quiet rows.
  if (totalTagCount(counters) === 0 && !showStar) return null;
  const { visible, overflowCount } = selectVisibleTags(counters);
  const fullDescription = describeTagCounts(counters);
  // aria / title: combine the counter description and the star
  // rationale when both are present so screen-reader users hear
  // both signals.
  const ariaLabel = showStar
    ? `${fullDescription}${fullDescription ? " · " : ""}starred (high quality after regen)`
    : fullDescription;
  return (
    <span className={styles.strip} title={ariaLabel} aria-label={ariaLabel}>
      {visible.map((spec) => {
        const value = counters[spec.field];
        return (
          <span key={spec.field} className={styles.pill} style={{ color: spec.color, borderColor: `${spec.color}66` }}>
            {spec.glyph ? <span aria-hidden="true">{spec.glyph}</span> : null}
            {spec.prefix ? <span>{spec.prefix}</span> : null}
            <span>×{value}</span>
          </span>
        );
      })}
      {overflowCount > 0 && <span className={styles.overflow}>+{overflowCount}</span>}
      {showStar && (
        <span className={styles.star} aria-hidden="true">
          ⭐
        </span>
      )}
    </span>
  );
};

export default SpineTagStrip;
